import { IdentityProviders, type UniqAppConfig } from 'korint-sdk/react';
import {
  BrokerageFirmUploadDocumentsModule,
  CreateRetailBrokerageFirmModule,
  DocumentUploadFunnelModule,
} from '@korint/folio';
import { ArrowUpDownIcon, BookTextIcon, InfoIcon, MailIcon, Table2 } from 'lucide-react';
import {
  contactInformationFields,
  informationFields,
  paymentMethodFields,
} from '../funnels/distributionRequestFields';
import { AvailableForProducts } from '../custom-pages/AvailableForProducts';
import { MyBrokerageFirm } from '../custom-pages/MyBrokerageFirm';
import { PolicyClaims } from '../custom-pages/PolicyClaims';
import { PolicyDrivequant } from '../custom-pages/PolicyDrivequant';
import { PolicyholderDrivequant } from '../custom-pages/PolicyholderDrivequant';
import { DrivequantSurchargeQuickActionDialog } from '../quick-actions/DrivequantSurchargeQuickActionDialog';
import { intermediaryInformationCards } from './intermediaryInformationCards';

const HIDDEN_DOCUMENT_CATEGORIES = ['audit-partenaire'];

export const uniqAppConfig = {
  title: 'Axece',
  favicon: '/axece-favicon.png',
  logoPath: '/axece-logo.png',
  colors: {
    primary: 'rgb(29, 25, 153)',
    primaryForeground: 'hsl(210deg 40% 98%)',
    secondary: 'rgb(29, 25, 153)',
    secondaryForeground: 'hsl(11 0% 100%)',
  },
  parentBrokerageFirmId: 'brokerage_firm_bCjgcgQCwiyTAiWhNVfFp',
  policyholderDashboard: {
    pages: [
      { type: 'home' },
      { type: 'information' },
      { type: 'documents' },
      { type: 'payment-methods' },
      { type: 'invoices' },
      {
        name: 'drivequant',
        type: 'custom',
        path: '/drivequant',
        icon: <Table2 className="size-4" />,
        component: (
          <AvailableForProducts productIds={['axeceVtc']}>
            <PolicyholderDrivequant />
          </AvailableForProducts>
        ),
      },
    ],
  },
  intermediaryDashboard: {
    pages: [
      { type: 'ongoing-new-business' },
      { type: 'portfolio' },
      { type: 'derogations', forTypes: ['WHOLESALE'] },
      { type: 'fraud-control' },
      { type: 'archived-new-business' },
      {
        name: 'sidebar.intermediary.myBrokerageFirm',
        type: 'custom',
        path: '/my-brokerage-firm',
        icon: <InfoIcon className="size-4" />,
        component: <MyBrokerageFirm />,
      },
      { type: 'intermediary-user-list' },
      { type: 'accounting' },
      { type: 'brokerage-firm-list', forTypes: ['WHOLESALE'] },
      { type: 'reports' },
      { type: 'commissions' },
      { type: 'products' },
      { type: 'analytics', forTypes: ['WHOLESALE'] },
      { type: 'conversations' },
      { type: 'general-inbox' },
    ],
    otherPages: [{ type: 'intermediary-user-form' }],
  },
  intermediaryDetailsPages: {
    intermediaryInformations: intermediaryInformationCards,
    intermediaryExtraDocuments: [{ category: 'beneficiary-change' }],
  },
  policyDashboard: {
    canModifySignedPolicy: ['WHOLESALE', 'RETAIL'],
    pages: [
      'home',
      'informations',
      'payment-methods',
      'documents',
      'invoices',
      { type: 'custom', path: '/claims', component: <PolicyClaims /> },
      'events',
      'notifications',
      'compliance',
      {
        type: 'custom',
        path: '/drivequant',
        component: (
          <AvailableForProducts productIds={['axeceVtc']}>
            <PolicyDrivequant />
          </AvailableForProducts>
        ),
      },
    ],
    quickActions: [
      {
        id: 'surchargePolicy',
        type: 'custom',
        icon: ArrowUpDownIcon,
        forProducts: ['axeceVtc'],
        forStatuses: ['POLICY_STARTED'],
        requiredActions: ['AXECE_SURCHARGE'],
        dialog: DrivequantSurchargeQuickActionDialog,
      },
      {
        id: 'generateInformationReport',
        type: 'publishEvent',
        eventType: 'AXECE_GENERATE_INFORMATION_REPORT',
        icon: BookTextIcon,
        forProducts: ['axeceVtc'],
        forStatuses: ['POLICY_STARTED', 'POLICY_SUSPENDED', 'POLICY_STOPPED'],
      },
      {
        id: 'missingDocumentsReminder',
        type: 'publishEvent',
        eventType: 'AXECE_MISSING_DOCUMENT',
        icon: MailIcon,
        forStatuses: ['POLICY_CREATED', 'POLICY_QUOTED'],
      },
    ],
  },
  intermediaryFunnelSteps: {
    steps: [
      // Not `module`: this one self-advances and has no footer of its own to pair with.
      {
        type: 'custom',
        path: 'creation',
        hidden: true,
        component: <CreateRetailBrokerageFirmModule />,
      },
      // Custom rather than `documentUploadFunnel` so `hiddenDocumentCategories` survives — the
      // declarative step carries only `context`, which would expose audit-partenaire to retail brokers.
      {
        type: 'custom',
        path: 'documents-upload',
        component: (
          <DocumentUploadFunnelModule
            context="brokerageFirm"
            hiddenDocumentCategories={HIDDEN_DOCUMENT_CATEGORIES}
          />
        ),
      },
      { type: 'intermediaryFunnel', path: 'information', fields: informationFields },
      {
        type: 'intermediaryFunnel',
        path: 'contact-information',
        fields: contactInformationFields,
      },
      {
        type: 'module',
        path: 'documents',
        module: (
          <BrokerageFirmUploadDocumentsModule
            hiddenDocumentCategories={HIDDEN_DOCUMENT_CATEGORIES}
          />
        ),
      },
      { type: 'intermediaryFunnel', path: 'payment-method', fields: paymentMethodFields },
      { type: 'distributionRequestFunnel', path: 'product' },
      { type: 'intermediaryFunnelEnd', path: 'end' },
    ],
  },
  login: {
    showLastLoginHint: true,
    providers: [IdentityProviders.GOOGLE, IdentityProviders.APPLE],
  },
  customFunnels: [
    {
      name: 'phygitalSignature',
      basePath: '/policies/:policyId/:branchId',
      layout: 'intermediary',
      steps: [
        {
          type: 'intermediarySignatureFunnel',
          path: 'phygital-signature',
          displayContext: 'policyholder',
        },
      ],
      afterEndPath: '/portfolio',
    },
  ],
} as const satisfies UniqAppConfig;
